import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div
        className="relative hidden w-1/2 bg-cover bg-center lg:block"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1613199928797-cde07ddeb2f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')" }}
      >
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="rounded-xl bg-white/95 p-5 shadow-lg" style={{ width: "fit-content" }}>
            <img src="/logo.webp" alt="The Retrofitter" data-testid="brand-logo" className="h-16 w-auto" />
          </div>
          <p className="mt-5 font-heading text-2xl font-black text-white">{t("login.brandSubtitle")}</p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-white p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center justify-between lg:justify-end">
            <div className="rounded-lg bg-slate-900 p-3 lg:hidden">
              <img src="/logo.webp" alt="The Retrofitter" className="h-9 w-auto" />
            </div>
            <LanguageSwitcher triggerClassName="border-slate-300 bg-white text-slate-700" />
          </div>

          <h2 className="font-heading text-3xl font-extrabold text-slate-900">{t("login.signIn")}</h2>
          <p className="mt-1 text-sm text-slate-500">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="overline text-slate-500">{t("login.email")}</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                required
                className="mt-1.5 h-12 border-slate-300 bg-white"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="overline text-slate-500">{t("login.password")}</Label>
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline" data-testid="forgot-link">
                  {t("login.forgot")}
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 border-slate-300 bg-white pr-11"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition-colors hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <Button data-testid="login-submit" type="submit" disabled={loading} className="h-12 w-full bg-blue-600 text-base hover:bg-blue-500">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("login.signIn")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
